import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/ui/pageHeader";
import { ArrowLeft } from "lucide-react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useCreateMeterModel, useUpdateMeterModel } from "@features/mx/manufacturer/hooks";
import { useMeterModelDetail, useMeterManufacturers } from "@features/mx/settings/hooks";
import { useGetResourceTypeList, useGetResourceSubtypeList, useGetUtilityServices } from "@features/mx/hooks";

const CONNECTION_SIZE_OPTIONS = [
  { label: "15mm", value: "15mm" },
  { label: "20mm", value: "20mm" },
  { label: "25mm", value: "25mm" },
  { label: "32mm", value: "32mm" },
  { label: "40mm", value: "40mm" },
  { label: "50mm", value: "50mm" },
];

const ACCURACY_OPTIONS = [
  { label: "Class A (±2%)", value: "Class A (±2%)" },
  { label: "Class B (±3%)", value: "Class B (±3%)" },
  { label: "Class C (±5%)", value: "Class C (±5%)" },
  { label: "±1%", value: "±1%" },
  { label: "±2%", value: "±2%" },
  { label: "±5%", value: "±5%" },
];

const IP_RATING_OPTIONS = [
  { label: "IP40", value: "IP40" },
  { label: "IP54", value: "IP54" },
  { label: "IP65", value: "IP65" },
  { label: "IP67", value: "IP67" },
  { label: "IP68", value: "IP68" },
];

const ENVIRONMENTAL_RATING_OPTIONS = [
  { label: "Indoor", value: "Indoor" },
  { label: "Outdoor", value: "Outdoor" },
  { label: "Submersible", value: "Submersible" },
  { label: "Weatherproof", value: "Weatherproof" },
];

const MOUNTING_TYPE_OPTIONS = [
  { label: "Wall Mount", value: "Wall Mount" },
  { label: "Pole Mount", value: "Pole Mount" },
  { label: "Flush Mount", value: "Flush Mount" },
  { label: "Surface Mount", value: "Surface Mount" },
  { label: "Pit Mount", value: "Pit Mount" },
  { label: "Inline", value: "Inline" },
];

const INSTALLATION_ORIENTATION_OPTIONS = [
  { label: "Horizontal", value: "Horizontal" },
  { label: "Vertical", value: "Vertical" },
  { label: "Angled", value: "Angled" },
];

const ENCLOSURE_RATING_OPTIONS = [
  { label: "IP20", value: "IP20" },
  { label: "IP40", value: "IP40" },
  { label: "IP54", value: "IP54" },
  { label: "IP55", value: "IP55" },
  { label: "IP65", value: "IP65" },
  { label: "IP66", value: "IP66" },
  { label: "IP67", value: "IP67" },
  { label: "IP68", value: "IP68" },
  { label: "IP69K", value: "IP69K" },
  { label: "NEMA 1", value: "NEMA 1" },
  { label: "NEMA 3R", value: "NEMA 3R" },
  { label: "NEMA 4", value: "NEMA 4" },
  { label: "NEMA 4X", value: "NEMA 4X" },
  { label: "NEMA 6", value: "NEMA 6" },
  { label: "NEMA 6P", value: "NEMA 6P" },
  { label: "NEMA 12", value: "NEMA 12" },
  { label: "NEMA 13", value: "NEMA 13" },
];

export default function AddModels() {
  const navigate = useNavigate();
  const { id } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();

  const { data: modelDetail } = useMeterModelDetail({
    remote_utility_id: remoteUtilityId,
    meter_model_id: id || "",
  });

  const { mutate: createModel } = useCreateMeterModel();
  const { mutate: updateModel } = useUpdateMeterModel();

  const [watchResourceType, setWatchResourceType] = useState<string>("");

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: resourceSubtypeResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );

  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.code,
      }))
      : [];
  }, [resourceTypeResponse]);

  const { data: manufacturerResponse } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId?.toString() || "",
    is_pagination_required: false,
  });

  const manufacturerOptions = React.useMemo(() => {
    const results = manufacturerResponse?.results || manufacturerResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      : [];
  }, [manufacturerResponse]);

  const { data: utilityServicesData } = useGetUtilityServices(remoteUtilityId);

  const utilityServiceOptions = React.useMemo(() => {
    return Array.isArray(utilityServicesData)
      ? utilityServicesData
        .filter((item: any) => item.isActive === true)
        .map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [utilityServicesData]);

  const initialValues = {
    resourceType: "",
    resourceSubType: "",
    manufacturer: "",
    modelName: "",
    makeCode: "",
    description: "",
    utilityServices: "",
    // Technical Specifications
    dialLength: 0,
    dialCount: 0,
    maxFlow: 0,
    connectionSize: "",
    accuracy: "",
    ipRating: "",
    // Physical Attributes
    material: "",
    weight: 0,
    dimensionL: 0,
    dimensionW: 0,
    dimensionH: 0,
    color: "",
    // Installation Specifications
    mountingType: "",
    orientation: "",
    enclosureRating: "",
    environmentalRating: "",
    // Environmental Parameters
    operatingTempMin: 0,
    operatingTempMax: 0,
    storageTempMin: 0,
    storageTempMax: 0,
  };

  const formFields: FormField[] = React.useMemo(() => [
    // General Information Group
    {
      name: "resourceType",
      label: "Resource Type",
      type: "select",
      required: true,
      placeholder: "Select resource type",
      helperText: "The category this meter model belongs to",
      options: resourceTypeOptions,
      group: "General Information",
      groupColorNeeded: false,
      className: "col-span-6",
    },
    {
      name: "resourceSubType",
      label: "Resource Sub Type",
      type: "select",
      required: false,
      placeholder: "Select resource sub type",
      helperText: "Select a resource type first",
      disabled: (formValues) => !formValues.resourceType,
      options: (() => {
        const results = resourceSubtypeResponse?.result ?? [];
        if (!Array.isArray(results)) return [];
        return results.map((item: any) => ({
          label: item.name,
          value: item.code,
        }));
      })(),
      group: "General Information",
      className: "col-span-6",
    },
    {
      name: "manufacturer",
      label: "Manufacturer",
      type: "select",
      required: true,
      placeholder: "Select manufacturer",
      helperText: "Choose the company that manufactures this meter model",
      options: manufacturerOptions,
      group: "General Information",
      className: "col-span-6",
    },
    {
      name: "modelName",
      label: "Model Name",
      type: "text",
      required: true,
      placeholder: "Enter model name",
      helperText: "The official product name or model number as specified by the manufacturer",
      group: "General Information",
      className: "col-span-6",
    },
    {
      name: "makeCode",
      label: "Make Code",
      type: "text",
      required: true,
      placeholder: "Enter make code",
      helperText: "Unique identifier for this model configuration",
      group: "General Information",
      className: "col-span-6",
    },
    {
      name: "utilityServices",
      label: "Utility Services",
      type: "select",
      required: true,
      placeholder: "Select utility services",
      helperText: "The services this model supports",
      options: utilityServiceOptions,
      group: "General Information",
      className: "col-span-6",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Add specifications notes or descriptions",
      group: "General Information",
      className: "col-span-12",
    },
    // Technical Specifications Group
    {
      name: "dialLength",
      label: "Dial Length",
      type: "number",
      required: false,
      placeholder: "e.g. 2",
      group: "Technical Specifications",
      className: "col-span-4",
      groupColorNeeded: false,
    },
    {
      name: "dialCount",
      label: "Dial Count",
      type: "number",
      required: false,
      placeholder: "e.g. 2",
      group: "Technical Specifications",
      className: "col-span-4",
    },
    {
      name: "maxFlow",
      label: "Max Flow",
      type: "number",
      required: false,
      placeholder: "e.g. 0.03",
      group: "Technical Specifications",
      className: "col-span-4",
    },
    {
      name: "connectionSize",
      label: "Connection Size",
      type: "select",
      required: false,
      placeholder: "Select connection size",
      options: CONNECTION_SIZE_OPTIONS,
      group: "Technical Specifications",
      className: "col-span-4",
    },
    {
      name: "accuracy",
      label: "Accuracy",
      type: "select",
      required: false,
      placeholder: "Select accuracy",
      options: ACCURACY_OPTIONS,
      group: "Technical Specifications",
      className: "col-span-4",
    },
    {
      name: "ipRating",
      label: "IP Rating",
      type: "select",
      required: false,
      placeholder: "Select IP rating",
      options: IP_RATING_OPTIONS,
      group: "Technical Specifications",
      className: "col-span-4",
    },
    // Physical Attributes Group
    {
      name: "material",
      label: "Material",
      type: "text",
      required: false,
      placeholder: "e.g. Polycarbonate Brass",
      group: "Physical Attributes",
      className: "col-span-4",
      groupColorNeeded: false,
    },
    {
      name: "weight",
      label: "Weight",
      type: "number",
      required: false,
      placeholder: "e.g. 500",
      group: "Physical Attributes",
      className: "col-span-4",
    },
    {
      name: "color",
      label: "Color",
      type: "text",
      required: false,
      placeholder: "e.g. Gray Blue",
      group: "Physical Attributes",
      className: "col-span-4",
    },
    {
      name: "dimensionL",
      label: "Length (mm)",
      type: "number",
      required: false,
      placeholder: "e.g. 150",
      group: "Physical Attributes",
      className: "col-span-4",
    },
    {
      name: "dimensionW",
      label: "Width (mm)",
      type: "number",
      required: false,
      placeholder: "e.g. 80",
      group: "Physical Attributes",
      className: "col-span-4",
    },
    {
      name: "dimensionH",
      label: "Height (mm)",
      type: "number",
      required: false,
      placeholder: "e.g. 100",
      group: "Physical Attributes",
      className: "col-span-4",
    },
    // Installation Specifications Group
    {
      name: "mountingType",
      label: "Mounting Type",
      type: "select",
      required: false,
      placeholder: "Select mounting type",
      options: MOUNTING_TYPE_OPTIONS,
      group: "Installation Specifications",
      className: "col-span-6",
      groupColorNeeded: false,
    },
    {
      name: "orientation",
      label: "Installation Orientation",
      type: "select",
      required: false,
      placeholder: "Select installation orientation",
      options: INSTALLATION_ORIENTATION_OPTIONS,
      group: "Installation Specifications",
      className: "col-span-6",
    },
    {
      name: "enclosureRating",
      label: "Enclosure Rating",
      type: "select",
      required: false,
      placeholder: "Select enclosure rating",
      options: ENCLOSURE_RATING_OPTIONS,
      group: "Installation Specifications",
      className: "col-span-6",
    },
    {
      name: "environmentalRating",
      label: "Environmental Rating",
      type: "select",
      required: false,
      placeholder: "Select environmental rating",
      options: ENVIRONMENTAL_RATING_OPTIONS,
      group: "Installation Specifications",
      className: "col-span-6",
    },
    // Environmental Parameters Group
    {
      name: "operatingTempMin",
      label: "Min Operating Temp (°C)",
      type: "number",
      required: false,
      placeholder: "e.g. -20",
      group: "Environmental Parameters",
      className: "col-span-3",
      groupColorNeeded: false,
    },
    {
      name: "operatingTempMax",
      label: "Max Operating Temp (°C)",
      type: "number",
      required: false,
      placeholder: "e.g. 60",
      group: "Environmental Parameters",
      className: "col-span-3",
    },
    {
      name: "storageTempMin",
      label: "Min Storage Temp (°C)",
      type: "number",
      required: false,
      placeholder: "e.g. -30",
      group: "Environmental Parameters",
      className: "col-span-3",
    },
    {
      name: "storageTempMax",
      label: "Max Storage Temp (°C)",
      type: "number",
      required: false,
      placeholder: "e.g. 70",
      group: "Environmental Parameters",
      className: "col-span-3",
    },
  ], [resourceTypeOptions, resourceSubtypeResponse, watchResourceType, manufacturerOptions, utilityServiceOptions]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: initialValues,
  });

  const currentResourceType = form.watch("resourceType");
  useEffect(() => {
    setWatchResourceType(currentResourceType || "");
  }, [currentResourceType]);

  useEffect(() => {
    if (id && modelDetail) {
      const detail = modelDetail?.result || modelDetail;
      const techSpecs = detail.technicalSpecifications || detail.technical_specifications || {};
      form.reset({
        resourceType: detail.resourceType || detail.resource_type || techSpecs.resourceType || "",
        resourceSubType: detail.resourceSubtype || detail.resource_subtype || detail.resourceSubType || detail.resource_sub_type || techSpecs.resourceSubType || "",
        manufacturer: detail.meterManufacturer?.id || detail.meter_manufacturer || "",
        modelName: detail.meterModel || detail.meter_model || "",
        makeCode: detail.meterMake || detail.meter_make || "",
        description: detail.description || "",
        utilityServices: detail.utilityService || detail.utility_service || "",
        // Technical Specifications
        dialLength: techSpecs.dialLength ?? 0,
        dialCount: techSpecs.dialCount ?? 0,
        maxFlow: techSpecs.maxFlow ?? 0,
        connectionSize: techSpecs.connectionSize || "",
        accuracy: techSpecs.accuracy || "",
        ipRating: techSpecs.ipRating || "",
        // Physical Attributes
        material: techSpecs.material || "",
        weight: techSpecs.weight ?? 0,
        dimensionL: techSpecs.length ?? 0,
        dimensionW: techSpecs.width ?? 0,
        dimensionH: techSpecs.height ?? 0,
        color: techSpecs.Color || techSpecs.color || "",
        // Installation Specifications
        mountingType: techSpecs.mountingType || "",
        orientation: techSpecs.installationOrientation || techSpecs.orientation || "",
        enclosureRating: techSpecs.enclosureRating || "",
        environmentalRating: techSpecs.environmentalRating || "",
        // Environmental Parameters
        operatingTempMin: techSpecs.storageTempratureRangeFrom ?? 0,
        operatingTempMax: techSpecs.storageTempratureRangeTo ?? 0,
        storageTempMin: techSpecs.operatingTempratureRangeFrom ?? 0,
        storageTempMax: techSpecs.operatingTempratureRangeTo ?? 0,
      });
    } else {
      form.reset(initialValues);
    }
  }, [id, modelDetail]);

  const handleSubmit = (data: any) => {
    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      meter_manufacturer: Number(data.manufacturer) || data.manufacturer,
      meter_make: data.makeCode,
      meter_model: data.modelName,
      resource_type: data.resourceType || "",
      resource_subtype: data.resourceSubType || "",
      description: data.description || "",
      utility_service: data.utilityServices || "",
      technical_specifications: {
        dial_length: Number(data.dialLength) || 0,
        dial_count: Number(data.dialCount) || 0,
        max_flow: Number(data.maxFlow) || 0,
        connection_size: data.connectionSize || "",
        weight: Number(data.weight) || 0,
        accuracy: data.accuracy || "",
        length: Number(data.dimensionL) || 0,
        width: Number(data.dimensionW) || 0,
        height: Number(data.dimensionH) || 0,
        material: data.material || "",
        ip_rating: data.ipRating || "",
        dimension: `${data.dimensionL || 0} * ${data.dimensionW || 0} * ${data.dimensionH || 0}`,
        Color: data.color || "",
        mounting_type: data.mountingType || "",
        installation_orientation: data.orientation || "",
        enclosure_rating: data.enclosureRating || "",
        environmental_rating: data.environmentalRating || "",
        operating_temprature_range_from:
          data.operatingTempMin !== undefined && data.operatingTempMin !== ""
            ? String(data.operatingTempMin)
            : "",
        operating_temprature_range_to:
          data.operatingTempMax !== undefined && data.operatingTempMax !== ""
            ? String(data.operatingTempMax)
            : "",
        storage_temprature_range_from:
          data.storageTempMin !== undefined && data.storageTempMin !== ""
            ? String(data.storageTempMin)
            : "",
        storage_temprature_range_to:
          data.storageTempMax !== undefined && data.storageTempMax !== ""
            ? String(data.storageTempMax)
            : "",
      },
    };

    if (id) {
      updateModel(
        { id: id, payload: payload as any },
        {
          onSuccess: () => {
            navigate("/mx/settings?tab=manufacturer-models&subtab=models");
          },
        }
      );
    } else {
      createModel(payload as any, {
        onSuccess: () => {
          navigate("/mx/settings?tab=manufacturer-models&subtab=models");
        },
      });
    }
  };

  return (
    <div className="p-3">
      <PageHeader
        title={id ? "Edit Model" : "Add New Model"}
        description={id ? "Modify details of the equipment model." : "Fill in the details to configure a new equipment model."}
        onBack={() => navigate("/mx/settings?tab=manufacturer-models&subtab=models")}
      />

      <div className="bg-white rounded-xl p-6 shadow-sm mt-4">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => navigate("/mx/settings?tab=manufacturer-models&subtab=models")}
          gridColumns={12}
          responsiveColumns={{ sm: 1, md: 12 }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: id ? "Save Changes" : "Add Model",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </div>
    </div>
  );
}
